import { useCallback, useEffect, useState } from 'react';
import {
  acquireFcmTokenIfPermitted,
  disableWebPush,
  getPushAvailability,
  isPushPreferenceEnabled,
  registerDeviceTokenWithApi,
  requestNotificationPermission,
  setPushPreferenceEnabled,
  type PushAvailability,
} from '../services/pushMessaging';
import { isNotificationGranted } from '../utils/pwaEnvironment';

export function useWebPush() {
  const [enabled, setEnabled] = useState(isPushPreferenceEnabled);
  const [availability, setAvailability] = useState<PushAvailability>(() => getPushAvailability());
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setAvailability(getPushAvailability());
    setEnabled(isPushPreferenceEnabled());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = useCallback(async (next: boolean) => {
    setErrorKey(null);
    if (next) {
      const latest = getPushAvailability();
      if (latest !== 'ready') {
        refresh();
        if (latest === 'permission_denied') {
          setErrorKey('notifications.pushPermissionDenied');
        } else if (latest === 'ios_needs_install') {
          setErrorKey('notifications.pushIosNeedsInstall');
        } else if (latest === 'not_configured') {
          setErrorKey('notifications.pushNotConfigured');
        } else {
          setErrorKey('notifications.pushEnableFailed');
        }
        setEnabled(false);
        return;
      }

      // Must run inside the click handler before other awaits (browser user-gesture rule).
      const permission = isNotificationGranted()
        ? 'granted'
        : await requestNotificationPermission();
      if (permission !== 'granted') {
        refresh();
        setErrorKey('notifications.pushPermissionDenied');
        setEnabled(false);
        return;
      }

      setBusy(true);
      try {
        setPushPreferenceEnabled(true);
        const token = await acquireFcmTokenIfPermitted();
        if (!token) {
          refresh();
          setErrorKey('notifications.pushEnableFailed');
          setEnabled(false);
          return;
        }
        await registerDeviceTokenWithApi(token);
        setEnabled(true);
        refresh();
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      await disableWebPush();
      setEnabled(false);
      refresh();
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  return { enabled, availability, busy, errorKey, toggle, refresh };
}
